import React, { useMemo, useState } from "react";
import { Check, Plus, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const BASE_PRODUCT_TYPES = [
  "Торт", "Бенто-торт", "Капкейки", "Печенье", "Пряники", "Тарт",
  "Тарталетки", "Эклеры", "Шу", "Пирожные", "Макарон", "Зефир",
  "Маршмеллоу", "Шоколад", "Конфеты", "Трюфели", "Кейк-попсы",
  "Муссовый десерт", "Чизкейк", "Рулет", "Кекс", "Павлова",
  "Десерт в стакане", "Набор десертов", "Другое",
];

export const normalizeProductTypeName = (value) => String(value || "")
  .normalize("NFKC")
  .trim()
  .toLocaleLowerCase("ru")
  .replace(/[‐‑‒–—-]+/g, " ")
  .replace(/\s+/g, " ");

export const productTypeOptions = (productTypes, selectedId) => {
  const groups = new Map();
  for (const item of productTypes || []) {
    const key = normalizeProductTypeName(item.name);
    if (!key) continue;
    const current = groups.get(key);
    if (!current || item.id === selectedId || (current.is_active === false && item.is_active !== false)) {
      groups.set(key, item);
    }
  }
  const baseKeys = new Set(BASE_PRODUCT_TYPES.map(normalizeProductTypeName));
  const base = BASE_PRODUCT_TYPES.map((label) => {
    const item = groups.get(normalizeProductTypeName(label));
    return { key: normalizeProductTypeName(label), label, item: item?.is_active === false ? null : item || null };
  });
  const custom = [...groups.entries()]
    .filter(([key, item]) => !baseKeys.has(key) && item.is_active !== false)
    .map(([key, item]) => ({ key, label: String(item.name).trim(), item }))
    .sort((a, b) => a.label.localeCompare(b.label, "ru"));
  return [...base, ...custom];
};

export default function ProductTypeField({ value, productTypes, onChange, onCreate, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const options = useMemo(() => productTypeOptions(productTypes, value), [productTypes, value]);
  const selected = (productTypes || []).find((item) => item.id === value);
  const selectedOption = selected && options.find((option) => option.key === normalizeProductTypeName(selected.name));
  const needle = normalizeProductTypeName(query);
  const filtered = options.filter((option) => !needle || normalizeProductTypeName(option.label).includes(needle));
  const exact = options.find((option) => option.key === needle);
  const createName = query.trim().replace(/\s+/g, " ");

  const choose = async (option) => {
    if (option.item) {
      onChange(option.item.id, option.item);
      setQuery("");
      setOpen(false);
      return;
    }
    setCreating(true);
    try {
      const created = await onCreate(option.label);
      if (created) onChange(created.id, created);
      setQuery("");
      setOpen(false);
    } finally {
      setCreating(false);
    }
  };
  const createCustom = async () => {
    if (!createName || exact) return;
    await choose({ label: createName });
  };

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-lg bg-white px-3 text-left text-[13px]"
          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: selected ? "#2A1D14" : "#8B6F4B" }}
          data-testid={testid}
        >
          <span className="truncate">{selectedOption?.label || selected?.name || "Выбрать или создать"}</span>
          <Search size={13} className="shrink-0 text-[#8B6F4B]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[260px] max-w-[calc(100vw-24px)] overflow-hidden border-[#EFE4D0] bg-white p-0">
        <div className="border-b border-[#EFE4D0] p-2">
          <div className="flex h-9 items-center gap-2 rounded-lg bg-[#FBF5EA] px-3 shadow-[inset_0_0_0_1px_#EFE4D0]">
            <Search size={13} className="shrink-0 text-[#8B6F4B]" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && createName && !exact) { event.preventDefault(); createCustom(); } }}
              placeholder="Найти тип изделия"
              className="min-w-0 flex-1 bg-transparent text-[13px] text-[#2A1D14] outline-none"
              data-testid={`${testid}-search`}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.map((option) => (
            <button key={option.key} type="button" onClick={() => choose(option)} disabled={creating}
              className={`flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-[#FBF5EA] ${option.item?.id === value ? "bg-[#F5EBD8]" : ""}`}
              style={{ color: "#2A1D14" }}>
              <span className="w-3 shrink-0">{option.item?.id === value && <Check size={12} className="text-[#3E5236]" />}</span>
              <span className="min-w-0 break-words">{option.label}</span>
            </button>
          ))}
          {createName && !exact && (
            <button type="button" onClick={createCustom} disabled={creating}
              className="flex min-h-11 w-full items-center gap-2 border-t border-[#EFE4D0] bg-[#F5EBD8]/60 px-3 py-2 text-left text-[13px] text-[#2A1D14] hover:bg-[#F5EBD8]">
              <Plus size={13} className="shrink-0" /> {creating ? "Создаём…" : <>Создать «{createName}»</>}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
